import React, { ComponentProps, ReactNode, useEffect, useState } from 'react'
import {
	Sheet,
	SheetTrigger,
	SheetContent,
} from '@cocoonspace/ui/components/sheet'
import { animate, motion, useMotionValue } from 'motion/react'

/** Visual Y needed to dismiss after release */
const DISMISS_DRAG_PX = 125

/** Finger movement is amplified so the sheet travels farther (elastic feel) */
const DRAG_GAIN = 10
// const DRAG_GAIN = 1.42

export const MobileDrawer = ({
	trigger,
	children,
	sheetProps = {},
	sheetContentProps = {},
}: {
	trigger: ReactNode
	children: ReactNode
	sheetProps?: ComponentProps<typeof Sheet>
	sheetContentProps?: ComponentProps<typeof SheetContent>
}) => {
	const [open, setOpen] = useState(sheetProps?.open ?? false)
	const y = useMotionValue(0)

	useEffect(() => {
		if (open) y.set(0)
	}, [open, y])

	const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
		const handle = event.currentTarget
		handle.setPointerCapture(event.pointerId)

		const startY = event.clientY
		const startOffset = y.get()

		const onPointerMove = (e: PointerEvent) => {
			const delta = e.clientY - startY
			const next = startOffset + delta * DRAG_GAIN
			y.set(Math.max(0, next))
		}

		const end = () => {
			handle.removeEventListener('pointermove', onPointerMove)
			handle.removeEventListener('pointerup', end)
			handle.removeEventListener('pointercancel', end)

			try {
				handle.releasePointerCapture(event.pointerId)
			} catch {
				// ignore
			}

			const current = y.get()

			if (current > DISMISS_DRAG_PX) {
				y.set(0)
				setOpen(false)
			} else {
				void animate(y, 0, {
					type: 'spring',
					stiffness: 420,
					damping: 24,
					mass: 0.9,
				})
			}
		}

		handle.addEventListener('pointermove', onPointerMove)
		handle.addEventListener('pointerup', end)
		handle.addEventListener('pointercancel', end)
	}

	return (
		<Sheet
			open={open}
			onOpenChange={setOpen}
		>
			<SheetTrigger asChild>{trigger}</SheetTrigger>

			<SheetContent
				side='bottom'
				asChild
				className='mx-auto gap-0 overflow-hidden rounded-t-2xl p-0 md:max-w-[400px]'
				{...sheetContentProps}
			>
				<motion.div
					style={{ y }}
					className='flex flex-col px-6 pb-6'
				>
					<div
						role='separator'
						aria-orientation='horizontal'
						className='flex cursor-grab touch-none justify-center py-3 active:cursor-grabbing'
						onPointerDown={onPointerDown}
					>
						<div className='bg-muted-foreground/30 h-1 w-10 shrink-0 rounded-full' />
					</div>

					{children}
				</motion.div>
			</SheetContent>
		</Sheet>
	)
}
